import { Button, classes, Icon } from '@dittolive/anvil'
import { CaretDownIcon } from '@phosphor-icons/react'
import React from 'react'
import { useToggle } from 'rooks'

import type { LiveQueryDialect } from '../../../types/dataBridge'
import DataBridgeQueryPreview from '../components/DataBridgeQueryPreview'

type Props = {
  /**
   * The query expression to display
   */
  expression: string | null | undefined
  /**
   * The query dialect (DQL or OQB/legacy)
   */
  dialect: LiveQueryDialect
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean
}

export default function DataBridgeQueryAccordion({
  expression,
  dialect,
  isLoading = false,
}: Props) {
  const [isOpen, toggleIsOpen] = useToggle()

  const renderLabel = () => {
    if (!isLoading) {
      return (
        <p
          data-testid="dataBridgeQueryAccordionLabel"
          className="text-foreground-normal text-base font-medium leading-5"
        >
          Expression
        </p>
      )
    } else {
      return (
        <div
          data-testid="dataBridgeQueryAccordionLabelPlaceholder"
          className="bg-background-surface-secondary h-5 w-1/5 animate-pulse rounded"
        />
      )
    }
  }

  const renderAtValue = () => {
    if (!isLoading) {
      const syntax = dialect === 'DQL' ? 'DQL' : 'Legacy syntax'
      return (
        <p
          className="text-foreground-subtle text-base leading-5"
          data-testid="dataBridgeQueryAccordionValue"
        >
          {syntax}
        </p>
      )
    } else {
      return (
        <div
          data-testid="dataBridgeAccordionPlaceholder"
          className="bg-background-surface-secondary h-5 w-2/5 animate-pulse rounded"
        />
      )
    }
  }

  return (
    <div>
      <Button
        variant="ghost"
        title={isOpen ? 'Hide query' : 'Show query'}
        className={classes(
          'bg-background-surface-secondary focus-visible:outline-focus-outline flex h-20 w-full items-center justify-between px-4 normal-case outline-none focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-60',
          { 'rounded-b-xl': !isOpen },
        )}
        onClick={() => toggleIsOpen()}
        disabled={isLoading}
        data-testid="dataBridgeQueryAccordion"
      >
        <div className="flex flex-1 flex-col items-start space-y-1">
          {renderLabel()}
          {renderAtValue()}
        </div>

        <span className="flex shrink-0">
          <Icon
            svg={<CaretDownIcon />}
            className={classes(
              'text-foreground-subtle size-4 transition-transform duration-150',
              isOpen ? 'rotate-180' : 'rotate-0',
            )}
          />
        </span>
      </Button>

      <div
        className={classes(
          'overflow-hidden rounded-b-xl transition-[max-height,opacity] duration-150 ease-in-out',
          isOpen ? 'max-h-37.5 opacity-100' : 'max-h-0 opacity-0',
        )}
        data-testid="dataBridgeQueryAccordionContent"
      >
        <DataBridgeQueryPreview expression={expression} />
      </div>
    </div>
  )
}
